import React, { createContext, useContext, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import { toast } from 'sonner';

export interface ImportSummary {
  inserted: number;
  updated: number;
  skipped: number;
  total_rows: number;
  skipped_log?: any[];
}

export interface UploadTask {
  id: string;
  fileName: string;
  status: 'processing' | 'completed' | 'error';
  progress: { current: number; total: number };
  summary?: ImportSummary;
  errorMsg?: string;
  createdAt: Date;
}

interface NotificationContextType {
  tasks: UploadTask[];
  isPanelOpen: boolean;
  togglePanel: () => void;
  openPanel: () => void;
  closePanel: () => void;
  addUploadTask: (taskId: string, fileName: string) => void;
  removeTask: (taskId: string) => void;
  clearCompleted: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [tasks, setTasks] = useState<UploadTask[]>([]);
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  const togglePanel = useCallback(() => setIsPanelOpen(p => !p), []);
  const openPanel = useCallback(() => setIsPanelOpen(true), []);
  const closePanel = useCallback(() => setIsPanelOpen(false), []);

  const removeTask = useCallback((taskId: string) => {
    setTasks(prev => prev.filter(t => t.id !== taskId));
  }, []);

  const clearCompleted = useCallback(() => {
    setTasks(prev => prev.filter(t => t.status === 'processing'));
  }, []);

  const addUploadTask = useCallback((taskId: string, fileName: string) => {
    // Add to state
    setTasks(prev => [
      {
        id: taskId,
        fileName,
        status: 'processing',
        progress: { current: 0, total: 100 },
        createdAt: new Date()
      },
      ...prev
    ]);

    const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
    const host = window.location.host;
    const basePath = (import.meta.env.VITE_BASE_PATH || '/').replace(/\/$/, '');
    const sseUrl = import.meta.env.DEV
      ? `${protocol}//localhost:8000/api/import/progress/${taskId}`
      : `${protocol}//${host}${basePath}/api/import/progress/${taskId}`;

    const eventSource = new EventSource(sseUrl, { withCredentials: true });

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        if (data.status === 'completed') {
          toast.success(`${fileName} uploaded successfully!`);
          eventSource.close();
        } else if (data.status === 'error') {
          toast.error(`Error processing ${fileName}: ${data.message}`);
          eventSource.close();
        }

        setTasks(prev => prev.map(task => {
          if (task.id !== taskId) return task;

          if (data.status === 'processing') {
            return { ...task, progress: { current: data.current, total: data.total } };
          } 
          
          if (data.status === 'completed') {
            return {
              ...task,
              status: 'completed',
              summary: {
                inserted: data.inserted || 0,
                updated: data.updated || 0,
                skipped: data.skipped || 0,
                total_rows: data.total_rows || 0,
                skipped_log: data.skipped_log || []
              }
            };
          }
          
          if (data.status === 'error') {
            return { ...task, status: 'error', errorMsg: data.message };
          }

          return task;
        }));
      } catch (err) {
        console.error('Failed to parse SSE message', err);
      }
    };

    eventSource.onerror = () => {
      setTasks(prev => prev.map(task => {
        if (task.id === taskId && task.status === 'processing') {
          toast.error(`Connection disconnected for ${fileName}`);
          eventSource.close();
          return { ...task, status: 'error', errorMsg: 'Connection lost' };
        }
        return task;
      }));
    };
  }, []);

  return (
    <NotificationContext.Provider
      value={{
        tasks,
        isPanelOpen,
        togglePanel,
        openPanel,
        closePanel,
        addUploadTask,
        removeTask,
        clearCompleted
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
